import axios from 'axios'
import { API, APP } from 'Config'
import { Events, News } from 'Types/emajinAPI'

import { signFile, uploadFile } from './files'
import { LoginParams, LoginResponse, Response } from './types'

async function login({ email, password, withRefresh = true }: LoginParams) {
  const result = await axios({
    baseURL: API.MAIN_URL,
    url: 'v1/auth/login',
    method: 'POST',
    data: {
      email,
      password,
      withRefresh,
    },
  })

  if (!result.data.ok) {
    return undefined
  }

  return {
    ok: result.data.ok,
    accessToken: result.data.accessToken,
    refreshToken: result.data.refreshToken,
  } as LoginResponse
}

export async function requestAuthCode(email: string, eventId?: string) {
  const result = await axios({
    baseURL: API.MAIN_URL,
    url: 'v1/auth/request-auth-code',
    method: 'POST',
    data: {
      organizationId: APP.ORGANIZATION_ID,
      email,
      origin: 'web',
      meta: JSON.stringify({ eventId }),
    },
  })

  return result.data as Response
}

export async function verifyAuthCode(
  email: string,
  code: string,
  withRefresh?: boolean,
) {
  const result = await axios({
    baseURL: API.MAIN_URL,
    url: 'v1/auth/verify-auth-code',
    method: 'POST',
    headers: {
      Host: API.MAIN_URL,
    },
    data: { email, code, withRefresh },
  })

  return {
    ok: result.data.ok,
    accessToken: result.data.accessToken,
    refreshToken: result.data.refreshToken,
  } as LoginResponse
}

export async function requestMagicLink(email: string, eventId?: string) {
  const result = await axios({
    baseURL: API.MAIN_URL,
    url: 'v1/auth/request-magic-link',
    method: 'POST',
    data: {
      organizationId: APP.ORGANIZATION_ID,
      email,
      origin: 'web',
      meta: JSON.stringify({ eventId }),
    },
  })

  return result.data as Response
}

export async function authWithMagicLink(token: string, withRefresh?: boolean) {
  const result = await axios({
    baseURL: API.MAIN_URL,
    url: 'v1/auth/magic-link-auth',
    method: 'POST',
    headers: {
      Host: API.MAIN_URL,
    },
    data: {
      token,
      withRefresh,
    },
  })

  return {
    ok: result.data.ok,
    accessToken: result.data.accessToken,
    refreshToken: result.data.refreshToken,
  } as LoginResponse
}

export async function getEvent(eventId: string) {
  return axios({
    baseURL: API.MAIN_URL,
    url: `v1/emajin/event/${eventId}`,
    method: 'GET',
  })
}

async function fetchFromApi<T>(endpoint: string): Promise<T> {
  const res = await axios({
    baseURL: API.MAIN_URL,
    url: endpoint,
    method: 'GET',
  })

  if (!res.data.ok) {
    throw new Error('Failed to fetch data')
  }

  return res.data
}

type GetEventsProps = {
  limit?: number
  offset?: number
  includePast?: boolean
}

async function getEvents({
  limit = 999,
  offset = 0,
  includePast = false,
}: GetEventsProps) {
  const query = `?limit=${limit}&offset=${offset}&includePast=${includePast}`
  return fetchFromApi<Events>(`/v1/emajin/events${query}`)
}

type GetNewsProps = {
  limit?: number
  offset?: number
}

async function getNews({ limit = 3, offset = 0 }: GetNewsProps) {
  const query = `?limit=${limit}&offset=${offset}`
  return fetchFromApi<News>(`/v1/emajin/news${query}`)
}

const APIService = {
  requestAuthCode,
  verifyAuthCode,
  authWithMagicLink,
  login,
  requestMagicLink,
  signFile,
  uploadFile,
  getEvent,
  getEvents,
  getNews,
}
export default APIService
